import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Crown, TrendingDown, TrendingUp, Trophy } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { EMPTY_FILTER, ListFilter, useFiltered } from "@/components/teacher/ListFilter";

const fmt = (iso) => new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const recText = (g) => `${g.title} ${g.mode || ""}`;

const Bars = ({ g }) => {
  const max = Math.max(...g.sessions.map((s) => Math.max(s.score, s.target || 0)), 1);
  return (
    <div className="flex items-end gap-2 h-40 overflow-x-auto pb-1" data-testid={`record-chart-${g.key}`}>
      {g.sessions.map((s, i) => {
        const best = s.score === g.best;
        return (
          <div key={i} className="flex flex-col items-center justify-end h-full min-w-[3.25rem]" title={fmt(s.at)}>
            <span className={`text-xs font-bold mb-1 ${best ? "text-[#FACC15]" : "text-slate-300"}`}>{s.score}</span>
            <div className={`w-8 rounded-t-lg origin-bottom anim-rise ${best ? "bg-[#FACC15] shadow-[0_0_16px_rgba(250,204,21,0.5)]" : "bg-[#3B82F6]/70"}`}
              style={{ height: `${Math.max(4, (s.score / max) * 100)}%`, animationDelay: `${i * 60}ms` }} />
            <span className="text-[10px] text-slate-500 mt-1">{new Date(s.at).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" })}</span>
          </div>
        );
      })}
    </div>
  );
};

const RecordCard = ({ g }) => {
  const prev = g.sessions.length > 1 ? g.sessions[g.sessions.length - 2].score : null;
  const diff = prev === null ? null : g.last - prev;
  return (
    <div className="panel p-5 space-y-4" data-testid={`record-group-${g.key}`}>
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-[200px]">
          <p className="eyebrow text-[#FACC15]">{g.mode} · {g.count} oturum</p>
          <p className="font-display text-2xl font-bold">{g.title}</p>
          <p className="text-xs text-slate-500">Oluşturan: <span className="text-slate-300">{g.owner_name}</span></p>
        </div>
        <div className="text-right">
          <p className="eyebrow flex items-center gap-1 justify-end"><Crown className="h-3.5 w-3.5 text-[#FACC15]" />Rekor</p>
          <p className="font-display text-4xl font-black neon-text-yellow" data-testid={`record-best-${g.key}`}>{g.best}</p>
          {diff !== null && (
            <p className={`text-xs font-semibold flex items-center gap-1 justify-end ${diff >= 0 ? "text-[#4ADE80]" : "text-red-400"}`}>
              {diff >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}Son oturum {diff >= 0 ? "+" : ""}{diff}
            </p>
          )}
        </div>
      </div>
      <Bars g={g} />
      <div className="divide-y divide-white/5 text-sm">
        {[...g.sessions].reverse().map((s, i) => (
          <div key={i} className="flex items-center gap-3 py-1.5" data-testid={`record-session-${g.key}-${i}`}>
            <span className="text-slate-400 w-40">{fmt(s.at)}</span>
            <span className="font-bold">{s.score} puan</span>
            {s.target > 0 && <span className={s.score >= s.target ? "text-[#4ADE80]" : "text-slate-500"}>Hedef {s.target} {s.score >= s.target ? "· ulaşıldı" : ""}</span>}
            {s.score === g.best && <Trophy className="h-4 w-4 text-[#FACC15] ml-auto" />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function RecordsPage() {
  const [groups, setGroups] = useState([]);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  useEffect(() => { api.get("/records").then((r) => setGroups(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const items = useMemo(() => groups.map((g) => ({ ...g, id: g.key, owned: true })), [groups]);
  const shown = useFiltered(items, filter, recText);
  return (
    <div className="space-y-8" data-testid="records-page">
      <div>
        <p className="eyebrow">Sınıf puanları</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Sınıf Rekorları</h1>
        <p className="text-slate-400 mt-1">Her oyunun sınıf toplam puanları tarihleriyle; sınıfın gelişimini buradan izleyin.</p>
      </div>
      {groups.length === 0 ? <div className="panel p-10 text-slate-400" data-testid="records-empty">Henüz biten oyun yok.</div> : <>
        <ListFilter items={items} value={filter} onChange={setFilter} placeholder="Oyun ara…" />
        <div className="grid xl:grid-cols-2 gap-5">{shown.map((g) => <RecordCard key={g.key} g={g} />)}</div>
      </>}
    </div>
  );
}
